import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Button,
  Tag,
  Radio,
  Space,
  Input,
  Slider,
  Checkbox,
  InputNumber,
  App
} from 'antd';
import {
  GoogleMap,
  LoadScript,
  Autocomplete,
  Circle
} from '@react-google-maps/api';
import {
  CheckOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import Signin from './Signin';

// Google Maps API Key (use the environment variable)
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const secrets = require('./secrets.js');
const api = secrets.server_url;

// Event categories options
const eventCategoryOptions = [
  { id: "islamic", label: "Islamic Events", color: "#1777FF" },
  { id: "social", label: "Social Events", color: "#52c41a" },
  { id: "sports", label: "Sports & Fitness", color: "#722ed1" },
  { id: "outdoor", label: "Outdoor Events", color: "#fa8c16" },
  { id: "arts", label: "Arts & Culture", color: "#eb2f96" },
  { id: "community", label: "Community Service", color: "#13c2c2" },
  { id: "social_justice", label: "Social Justice", color: "#faad14" },
  { id: "educational", label: "Educational", color: "#2f54eb" },
  { id: "networking", label: "Networking", color: "#fa541c" },
  { id: "technology", label: "Technology", color: "#722ed1" },
  { id: "food", label: "Food", color: "#a0d911" },
  { id: "gaming", label: "Gaming", color: "#eb2f96" },
  { id: "sister_friendly", label: "Sister-friendly", color: "#1890ff" },
  { id: "revert", label: "Revert", color: "#fa8c16" },
  { id: "matrimonial", label: "Matrimonial", color: "#f5222d" }
];

// Event types options
const eventTypeOptions = [
  { id: "basketball", label: "Basketball", color: "#1777FF" },
  { id: "chess", label: "Chess", color: "#262626" },
  { id: "hackathons", label: "Hackathons", color: "#722ed1" },
  { id: "quran_study", label: "Qur'an Study", color: "#52c41a" },
  { id: "hiking", label: "Hiking", color: "#fa8c16" },
  { id: "coffee_meetings", label: "Coffee Meetings", color: "#faad14" },
  { id: "workshops", label: "Workshops", color: "#13c2c2" },
  { id: "lectures", label: "Lectures", color: "#2f54eb" },
  { id: "charity_drives", label: "Charity Drives", color: "#eb2f96" },
  { id: "art_exhibits", label: "Art Exhibits", color: "#fa541c" },
  { id: "conferences", label: "Conferences", color: "#1890ff" },
  { id: "dinners", label: "Dinners", color: "#a0d911" },
];

// Event goals options
const eventGoalOptions = [
  { id: "faith", label: "Increasing my Faith", color: "#1777FF" },
  { id: "friends", label: "Making new friends", color: "#52c41a" },
  { id: "exploring", label: "Exploring my city", color: "#fa8c16" },
  { id: "deals", label: "Finding local deals", color: "#faad14" },
  { id: "fun", label: "Having fun", color: "#f5222d" },
  { id: "learning", label: "Learning new things", color: "#722ed1" },
  { id: "career", label: "Advancing my career", color: "#2f54eb" },
  { id: "helping", label: "Helping the community", color: "#13c2c2" },
];

// Event scale options
const eventScaleOptions = [
  { value: 4, label: "A large, high-energy event with lots of people" },
  { value: 3, label: "A medium-sized event with a mix of new and familiar faces" },
  { value: 2, label: "A small hangout or discussion where people can have meaningful conversations" },
  { value: 1, label: "One-on-one or just with a couple of friends" }
];

// Social comfort options
const socialComfortOptions = [
  { value: 4, label: "Excited – I love meeting new folks" },
  { value: 3, label: "Comfortable – I'll mingle after warming up" },
  { value: 2, label: "A bit anxious – I prefer when a friend comes along" },
  { value: 1, label: "Very reserved – Large crowds aren't my thing" }
];

// Similarity importance options
const similarityOptions = [
  { value: 1, label: "Not important – I'm comfortable with all ages and types of people" },
  { value: 2, label: "Somewhat – I like diversity but also hope to find a few people I relate to" },
  { value: 3, label: "Very important – I connect best with my demographic" }
];

// Budget preference options
const budgetOptions = [
  { value: 4, label: "No real budget limit for great experiences" },
  { value: 3, label: "I prefer lower-cost events, but I'm willing to pay for something worthwhile" },
  { value: 2, label: "I only attend low-cost events" },
  { value: 1, label: "I only attend free events" }
];

// Time slots for availability
const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const timeSlots = ["Morning", "Afternoon", "Evening"];

// Libraries for Google Maps
const libraries = ["places"];

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px',
};

function OnboardingQuiz() {
  const navigate = useNavigate();
  const { message, notification } = App.useApp();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0); // index of the current question
  const [totalQuestions] = useState(10); // Updated to match the total number of questions

  // Form data state
  const [selectedEventCategories, setSelectedEventCategories] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [selectedEventGoals, setSelectedEventGoals] = useState([]);
  const [eventScale, setEventScale] = useState(null);
  const [socialComfort, setSocialComfort] = useState(null);
  const [similarityImportance, setSimilarityImportance] = useState(null);

  // User info
  const [age, setAge] = useState(null);
  const [gender, setGender] = useState(null);
  const [occupation, setOccupation] = useState('');
  const [mixedGenderPreference, setMixedGenderPreference] = useState(null);

  // Location
  const [locationQuery, setLocationQuery] = useState('');
  const [locationCity, setLocationCity] = useState('');
  const [locationCoordinates, setLocationCoordinates] = useState(null);
  const [travelRadius, setTravelRadius] = useState(15);
  const [isWillingToTravelFurther, setIsWillingToTravelFurther] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 37.7749, lng: -122.4194 }); // Default to San Francisco

  // Availability
  const [availability, setAvailability] = useState([]);
  const [isWillingToMakeTime, setIsWillingToMakeTime] = useState(false);

  // Budget
  const [budgetPreference, setBudgetPreference] = useState(null);

  // UI state
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showSigninModal, setShowSigninModal] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [scrollToIndex, setScrollToIndex] = useState(null); // index to scroll to
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isProgrammaticScrolling, setIsProgrammaticScrolling] = useState(false);

  // References to question containers for scrolling - using object instead of array
  const questionRefs = useRef({});

  // Set question ref function - this ensures consistent refs for each question
  const setQuestionRef = (index) => (el) => {
    if (el) {
      questionRefs.current[index] = el;
    }
  };

  // Check if user is logged in
  useEffect(() => {
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      setIsUserLoggedIn(true);
    }
  }, []);

  // Debug quiz completed state changes
  useEffect(() => {
    console.log("Quiz completed state changed:", quizCompleted);
  }, [quizCompleted]);

  // Improved scroll handling with smoother transitions
  useEffect(() => {
    let scrollTimeout = null;
    let lastScrollPosition = window.scrollY;
    let scrollThreshold = 10; // Lower threshold to be more responsive
    let isScrolling = false;

    const handleScroll = () => {
      // Skip scroll handling during programmatic scrolling
      if (isProgrammaticScrolling) return;

      // Clear any existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Calculate scroll distance from last position
      const currentScrollPosition = window.scrollY;
      const scrollDistance = Math.abs(currentScrollPosition - lastScrollPosition);

      // Set a flag to indicate user is actively scrolling
      isScrolling = true;

      // Set a timeout to handle the end of scrolling
      scrollTimeout = setTimeout(() => {
        isScrolling = false;

        // Get all question positions
        const positions = Object.entries(questionRefs.current).map(([index, el]) => {
          if (!el) return { index: parseInt(index), distance: Infinity };
          const rect = el.getBoundingClientRect();
          const distanceFromCenter = Math.abs(rect.top + (rect.height / 2) - window.innerHeight / 2);
          return { index: parseInt(index), distance: distanceFromCenter, el };
        });

        // Find the closest question to center
        const closestQuestion = positions.reduce(
          (closest, current) => current.distance < closest.distance ? current : closest,
          { index: 0, distance: Infinity }
        );

        // Only update if we found a valid question
        if (closestQuestion.distance !== Infinity) {
          // Update current question index if different
          if (closestQuestion.index !== currentQuestionIndex) {
            setCurrentQuestionIndex(closestQuestion.index);
          }

          // Auto-center if user has scrolled more than the threshold
          if (scrollDistance > scrollThreshold) {
            const el = questionRefs.current[closestQuestion.index];
            if (el) {
              // Set flag to prevent scroll handler from interfering
              setIsProgrammaticScrolling(true);

              // Calculate position
              const elTop = el.offsetTop;
              const elHeight = el.offsetHeight;
              const windowHeight = window.innerHeight;
              const scrollTo = elTop - (windowHeight / 2) + (elHeight / 2);

              // Use requestAnimationFrame for smoother animation
              const startPosition = window.scrollY;
              const distance = scrollTo - startPosition;
              const duration = 800; // Longer duration for smoother effect
              let startTime;

              // Easing function for smoother acceleration/deceleration
              const easeInOutCubic = (t) =>
                t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;

              const animateScroll = (timestamp) => {
                if (!startTime) startTime = timestamp;
                const elapsed = timestamp - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const eased = easeInOutCubic(progress);

                window.scrollTo(0, startPosition + distance * eased);

                if (elapsed < duration) {
                  requestAnimationFrame(animateScroll);
                } else {
                  // Animation complete
                  setIsProgrammaticScrolling(false);
                  lastScrollPosition = window.scrollY;
                }
              };

              requestAnimationFrame(animateScroll);
            }
          } else {
            // Update last scroll position if we didn't auto-center
            lastScrollPosition = currentScrollPosition;
          }
        }
      }, 150); // Shorter debounce time for more responsiveness
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [currentQuestionIndex, isProgrammaticScrolling]);

  // Improved scroll-to-question effect
  useEffect(() => {
    if (scrollToIndex !== null) {
      const el = questionRefs.current[scrollToIndex];
      if (el) {
        // Set flag to prevent scroll handler from interfering
        setIsProgrammaticScrolling(true);

        // Calculate position
        const elTop = el.offsetTop;
        const elHeight = el.offsetHeight;
        const windowHeight = window.innerHeight;
        const scrollTo = elTop - (windowHeight / 2) + (elHeight / 2);

        // Smooth scroll
        window.scrollTo({ top: scrollTo, behavior: 'smooth' });

        // Reset the scroll index
        setScrollToIndex(null);

        // Reset the programmatic scrolling flag after scrolling completes
        setTimeout(() => {
          setIsProgrammaticScrolling(false);
        }, 500); // Wait longer than the scroll animation
      } else {
        // If element not found, just clear the scroll index
        setScrollToIndex(null);
      }
    }
  }, [scrollToIndex]);

  // Updated goToQuestion function 
  const goToQuestion = (index) => {
    // Prevent out-of-bounds
    if (index < 0) return;

    // If we're trying to go beyond the last question, show the completion screen
    if (index >= totalQuestions) {
      // Check if all questions are answered before showing completion
      if (allQuestionsAnswered()) {
        setQuizCompleted(true);
      } else {
        notification.warning({
          message: 'Please complete all questions',
          description: 'You need to answer all questions before completing the quiz.',
          placement: 'top',
          duration: 3,
        });
      }
      return;
    }

    // Prevent going to future questions if previous ones aren't answered
    if (index > currentQuestionIndex) {
      // Check if all previous questions up to current are answered
      for (let i = 0; i <= currentQuestionIndex; i++) {
        if (!checkQuestionAnswered(i)) {
          notification.warning({
            message: 'Please complete the current question',
            description: 'You need to answer all previous questions before moving forward.',
            placement: 'top',
            duration: 3,
          });
          return;
        }
      }
    }

    // Allow freely going back to previous answered questions
    setCurrentQuestionIndex(index);
    setScrollToIndex(index);

    // Reset the quiz completed state when navigating back from completion
    if (quizCompleted) {
      setQuizCompleted(false);
    }
  };



  // Handle tag selection (multiple selection)
  const toggleTag = (collection, setCollection, id) => {
    if (collection.includes(id)) {
      setCollection(collection.filter(item => item !== id));
    } else {
      setCollection([...collection, id]);
    }
  };

  // Handle location selection from Google Maps Autocomplete
  const onPlaceSelected = () => {
    const place = autocomplete.getPlace();

    if (place && place.geometry && place.geometry.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      // Find the city name from address components
      let city = '';
      if (place.address_components) {
        for (const component of place.address_components) {
          if (component.types.includes('locality')) {
            city = component.long_name;
            break;
          }
        }
      }

      setLocationCoordinates({ lat, lng });
      setMapCenter({ lat, lng });
      setLocationCity(city || place.name || '');
    }
  };

  // Handle availability selection
  const toggleAvailability = (day, time) => {
    const timeSlot = `${day} ${time}`;
    if (availability.includes(timeSlot)) {
      setAvailability(availability.filter(slot => slot !== timeSlot));
    } else {
      setAvailability([...availability, timeSlot]);
    }
  };

  // Check if can continue to next question
  const canContinue = () => {
    switch (currentQuestionIndex) {
      case 0: // Event Categories
        return selectedEventCategories.length > 0;
      case 1: // Event Types
        return selectedEventTypes.length > 0;
      case 2: // Event Goals
        return selectedEventGoals.length > 0;
      case 3: // Event Scale
        return eventScale !== null;
      case 4: // Social Comfort
        return socialComfort !== null;
      case 5: // Personal Info
        return age !== null && gender !== null && occupation.trim() !== '' && mixedGenderPreference !== null;
      case 6: // Location
        return locationCoordinates !== null;
      case 7: // Availability
        return availability.length > 0;
      case 8: // Budget
        return budgetPreference !== null;
      case 9: // Similarity
        return similarityImportance !== null;
      default:
        return true;
    }
  };

  // Check if all required fields are completed to enable the final submission button
  const allQuestionsAnswered = () => {
    return (
      selectedEventCategories.length > 0 &&
      selectedEventTypes.length > 0 &&
      selectedEventGoals.length > 0 &&
      eventScale !== null &&
      socialComfort !== null &&
      age !== null &&
      gender !== null &&
      occupation.trim() !== '' &&
      mixedGenderPreference !== null &&
      locationCoordinates !== null &&
      availability.length > 0 &&
      budgetPreference !== null &&
      similarityImportance !== null
    );
  };

  // Format quiz data according to schema
  const formatQuizData = () => {
    return {
      event_categories: selectedEventCategories,
      event_types: selectedEventTypes,
      event_goals: selectedEventGoals,
      eventScale,
      socialComfort,
      similarityImportance,
      age,
      gender: gender === 'male',
      occupation,
      mixedGenderPreference: mixedGenderPreference === 'yes',
      locationCity,
      locationCoordinates: {
        lat: locationCoordinates?.lat,
        lon: locationCoordinates?.lng
      },
      travelRadius,
      isWillingToTravelFurther,
      availability,
      isWillingToMakeTime,
      budgetPreference
    };
  };

  // Handle saving quiz data to backend
  const saveQuizToBackend = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const quizData = formatQuizData();

      // Save to localStorage first as a backup
      localStorage.setItem('quizPreferences', JSON.stringify(quizData));

      // Call the API endpoint
      const response = await axios.post(
        `${api}user/quiz-response`,
        { quizData },
        {
          withCredentials: true,
          headers: {
            'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        notification.success({
          message: 'Success',
          description: 'Your preferences have been saved successfully!',
        });

        // Redirect to home page with fromQuiz flag to trigger phone number modal if needed
        navigate('/', { state: { fromQuiz: true } });
      } else {
        setSubmitError('Failed to save preferences. Please try again.');
      }
    } catch (error) {
      console.error('Error saving quiz preferences:', error);
      setSubmitError(
        error.response?.data?.message ||
        'Something went wrong. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle signing in after completing quiz
  const handleSigninComplete = (success) => {
    if (success) {
      // User successfully signed in, save quiz data
      setIsUserLoggedIn(true);
      saveQuizToBackend();
    }
    // Close the signin modal either way
    setShowSigninModal(false);
  };

  // Handle completing the quiz
  const handleComplete = () => {
    console.log("handleComplete called", { isUserLoggedIn, allQuestionsAnswered: allQuestionsAnswered() });

    if (!allQuestionsAnswered()) {
      // Scroll to the first unanswered question
      for (let i = 0; i < totalQuestions; i++) {
        const isAnswered = checkQuestionAnswered(i);
        if (!isAnswered) {
          console.log("Found unanswered question:", i);
          goToQuestion(i);
          return;
        }
      }
      return;
    }

    // If user is already logged in, just save the data
    if (isUserLoggedIn) {
      console.log("User is logged in, saving directly");
      saveQuizToBackend();
    } else {
      // Otherwise show the signin modal
      console.log("User is not logged in, showing sign-in modal");
      setShowSigninModal(true);
    }
  };

  // Helper to check if a specific question is answered
  const checkQuestionAnswered = (questionIndex) => {
    switch (questionIndex) {
      case 0: // Event Categories
        return selectedEventCategories.length > 0;
      case 1: // Event Types
        return selectedEventTypes.length > 0;
      case 2: // Event Goals
        return selectedEventGoals.length > 0;
      case 3: // Event Scale
        return eventScale !== null;
      case 4: // Social Comfort
        return socialComfort !== null;
      case 5: // Personal Info
        return age !== null && gender !== null && occupation.trim() !== '' && mixedGenderPreference !== null;
      case 6: // Location
        return locationCoordinates !== null;
      case 7: // Availability
        return availability.length > 0;
      case 8: // Budget
        return budgetPreference !== null;
      case 9: // Similarity
        return similarityImportance !== null;
      default:
        return true;
    }
  };

  return (
    // <div className="wrapper">
    //   <Navbar />
    <>
      <div className="quiz-container">
        <div className="quiz-content">
          {/* Question Dots Navigation - Now sticky without a box */}
          <div className="question-dots-nav">
            {Array.from({ length: totalQuestions }).map((_, index) => (
              <div
                key={index}
                className={`question-dot ${currentQuestionIndex === index ? 'active' : ''} ${checkQuestionAnswered(index) ? 'completed' : ''}`}
                onClick={() => goToQuestion(index)}
                title={`Question ${index + 1}`}
              />
            ))}
          </div>

          {/* Question 1: Event Categories */}
          {currentQuestionIndex >= 0 && (
            <div
              ref={setQuestionRef(0)}
              className={`question-slide ${currentQuestionIndex === 0 ? 'active' : ''}`}
            >
              <div className="question-header">
                <div className="question-number">Question 1 of {totalQuestions}</div>
                <h2 className="question-title">What types of events are you interested in?</h2>
                <p className="question-description">Select all categories that apply.</p>
              </div>

              <div className="event-type-options">
                {eventCategoryOptions.map((category) => (
                  <Tag
                    key={category.id}
                    className={`event-type-tag ${selectedEventCategories.includes(category.id) ? 'selected' : ''}`}
                    onClick={() => toggleTag(selectedEventCategories, setSelectedEventCategories, category.id)}
                    style={{
                      backgroundColor: selectedEventCategories.includes(category.id) ? category.color : 'transparent',
                      color: selectedEventCategories.includes(category.id) ? 'white' : '#595959',
                      borderColor: category.color
                    }}
                  >
                    {category.label}
                  </Tag>
                ))}
              </div>

              <div className="quiz-navigation">
                <Link
                  to="/"
                  className="nav-button exit-button"
                >
                  Exit
                </Link>
                <button
                  className="nav-button back-button"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  Back
                </button>
                <button
                  className="nav-button continue-button"
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={!checkQuestionAnswered(currentQuestionIndex)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Question 2: Event Types */}
          {currentQuestionIndex >= 1 && (
            <div
              ref={setQuestionRef(1)}
              className={`question-slide ${currentQuestionIndex === 1 ? 'active' : ''}`}
            >
              <div className="question-header">
                <div className="question-number">Question 2 of {totalQuestions}</div>
                <h2>What specific types of events do you enjoy?</h2>
                <p className="question-description">Select all that interest you.</p>
              </div>

              <div className="event-type-options">
                {eventTypeOptions.map((type) => (
                  <Tag
                    key={type.id}
                    className={`event-type-tag ${selectedEventTypes.includes(type.id) ? 'selected' : ''}`}
                    onClick={() => toggleTag(selectedEventTypes, setSelectedEventTypes, type.id)}
                    style={{
                      backgroundColor: selectedEventTypes.includes(type.id) ? type.color : 'transparent',
                      color: selectedEventTypes.includes(type.id) ? 'white' : '#595959',
                      borderColor: type.color
                    }}
                  >
                    {type.label}
                  </Tag>
                ))}
              </div>

              <div className="quiz-navigation">
                <Link
                  to="/"
                  className="nav-button exit-button"
                >
                  Exit
                </Link>
                <button
                  className="nav-button back-button"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  Back
                </button>
                <button
                  className="nav-button continue-button"
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={!checkQuestionAnswered(currentQuestionIndex)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Question 3: Event Goals */}
          {currentQuestionIndex >= 2 && (
            <div
              ref={setQuestionRef(2)}
              className={`question-slide ${currentQuestionIndex === 2 ? 'active' : ''}`}
            >
              <div className="question-header">
                <div className="question-number">Question 3 of {totalQuestions}</div>
                <h2>What are your goals for attending events?</h2>
                <p className="question-description">Select all that apply to you.</p>
              </div>

              <div className="event-type-options">
                {eventGoalOptions.map((goal) => (
                  <Tag
                    key={goal.id}
                    className={`event-type-tag ${selectedEventGoals.includes(goal.id) ? 'selected' : ''}`}
                    onClick={() => toggleTag(selectedEventGoals, setSelectedEventGoals, goal.id)}
                    style={{
                      backgroundColor: selectedEventGoals.includes(goal.id) ? goal.color : 'transparent',
                      color: selectedEventGoals.includes(goal.id) ? 'white' : '#595959',
                      borderColor: goal.color
                    }}
                  >
                    {goal.label}
                  </Tag>
                ))}
              </div>

              <div className="quiz-navigation">
                <Link
                  to="/"
                  className="nav-button exit-button"
                >
                  Exit
                </Link>
                <button
                  className="nav-button back-button"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  Back
                </button>
                <button
                  className="nav-button continue-button"
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={!checkQuestionAnswered(currentQuestionIndex)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Question 4: Event Scale */}
          {currentQuestionIndex >= 3 && (
            <div
              ref={setQuestionRef(3)}
              className={`question-slide ${currentQuestionIndex === 3 ? 'active' : ''}`}
            >
              <div className="question-header">
                <div className="question-number">Question 4 of {totalQuestions}</div>
                <h2>Which of these events sounds the most fun to you?</h2>
                <p className="question-description">Select one option.</p>
              </div>

              <div className="radio-options">
                <Radio.Group
                  onChange={(e) => setEventScale(e.target.value)}
                  value={eventScale}
                  className="radio-group"
                >
                  <Space direction="vertical">
                    {eventScaleOptions.map(option => (
                      <Radio
                        key={option.value}
                        value={option.value}
                        className="radio-option"
                      >
                        {option.label}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </div>

              <div className="quiz-navigation">
                <Link
                  to="/"
                  className="nav-button exit-button"
                >
                  Exit
                </Link>
                <button
                  className="nav-button back-button"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  Back
                </button>
                <button
                  className="nav-button continue-button"
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={!checkQuestionAnswered(currentQuestionIndex)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Question 5: Social Comfort */}
          {currentQuestionIndex >= 4 && (
            <div
              ref={setQuestionRef(4)}
              className={`question-slide ${currentQuestionIndex === 4 ? 'active' : ''}`}
            >
              <div className="question-header">
                <div className="question-number">Question 5 of {totalQuestions}</div>
                <h2>How do you typically feel at large gatherings?</h2>
                <p className="question-description">Select the option that best describes you.</p>
              </div>

              <div className="radio-options">
                <Radio.Group
                  onChange={(e) => setSocialComfort(e.target.value)}
                  value={socialComfort}
                  className="radio-group"
                >
                  <Space direction="vertical">
                    {socialComfortOptions.map(option => (
                      <Radio
                        key={option.value}
                        value={option.value}
                        className="radio-option"
                      >
                        {option.label}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </div>

              <div className="quiz-navigation">
                <Link
                  to="/"
                  className="nav-button exit-button"
                >
                  Exit
                </Link>
                <button
                  className="nav-button back-button"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  Back
                </button>
                <button
                  className="nav-button continue-button"
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={!checkQuestionAnswered(currentQuestionIndex)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Question 6: Basic Information */}
          {currentQuestionIndex >= 5 && (
            <div
              ref={setQuestionRef(5)}
              className={`question-slide ${currentQuestionIndex === 5 ? 'active' : ''}`}
            >
              <div className="question-header">
                <div className="question-number">Question 6 of {totalQuestions}</div>
                <h2>Tell us a bit about yourself</h2>
                <p className="question-description">This helps us tailor event recommendations to you.</p>
              </div>

              <div className="form-fields">
                <div className="form-group">
                  <label>Age</label>
                  <InputNumber
                    min={13}
                    max={100}
                    value={age}
                    onChange={setAge}
                    placeholder="Your age"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Gender</label>
                  <Radio.Group
                    onChange={(e) => setGender(e.target.value)}
                    value={gender}
                    className="form-radio-group"
                  >
                    <Radio value="male">Male</Radio>
                    <Radio value="female">Female</Radio>
                  </Radio.Group>
                </div>

                <div className="form-group">
                  <label>Occupation</label>
                  <Input
                    placeholder="Your occupation"
                    value={occupation}
                    onChange={(e) => setOccupation(e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Do you prefer mixed-gender events?</label>
                  <Radio.Group
                    onChange={(e) => setMixedGenderPreference(e.target.value)}
                    value={mixedGenderPreference}
                    className="form-radio-group"
                  >
                    <Radio value="yes">Yes</Radio>
                    <Radio value="no">No</Radio>
                  </Radio.Group>
                </div>
              </div>

              <div className="quiz-navigation">
                <Link
                  to="/"
                  className="nav-button exit-button"
                >
                  Exit
                </Link>
                <button
                  className="nav-button back-button"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  Back
                </button>
                <button
                  className="nav-button continue-button"
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={!checkQuestionAnswered(currentQuestionIndex)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Question 7: Location */}
          {currentQuestionIndex >= 6 && (
            <div
              ref={setQuestionRef(6)}
              className={`question-slide ${currentQuestionIndex === 6 ? 'active' : ''}`}
            >
              <div className="question-header">
                <div className="question-number">Question 7 of {totalQuestions}</div>
                <h2>Where are you located?</h2>
                <p className="question-description">This helps us find events near you.</p>
              </div>

              <div className="location-section">
                <LoadScript
                  googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                  libraries={libraries}
                >
                  <div className="location-search">
                    <Autocomplete
                      onLoad={(autocomplete) => setAutocomplete(autocomplete)}
                      onPlaceChanged={onPlaceSelected}
                    >
                      <Input
                        prefix={<EnvironmentOutlined />}
                        placeholder="Enter your city, neighborhood, or address"
                        value={locationQuery}
                        onChange={(e) => setLocationQuery(e.target.value)}
                        className="location-input"
                      />
                    </Autocomplete>
                  </div>

                  {locationCoordinates && (
                    <>
                      <div className="map-container">
                        <GoogleMap
                          mapContainerStyle={mapContainerStyle}
                          center={mapCenter}
                          zoom={10}
                          options={{
                            mapTypeControl: false,
                            streetViewControl: false,
                            fullscreenControl: false,
                            zoomControl: true,
                            scrollwheel: true,
                            mapTypeId: "roadmap"
                          }}
                        >
                          <Circle
                            center={mapCenter}
                            radius={travelRadius * 1609.34} // Convert miles to meters
                            options={{
                              fillColor: '#1777FF',
                              fillOpacity: 0.2,
                              strokeColor: '#1777FF',
                              strokeOpacity: 0.8,
                              strokeWeight: 2,
                            }}
                          />
                        </GoogleMap>
                      </div>

                      <div className="radius-slider">
                        <label>How far are you willing to travel? <span className="radius-value">{travelRadius} miles</span></label>
                        <Slider
                          min={1}
                          max={50}
                          value={travelRadius}
                          onChange={setTravelRadius}
                        />
                      </div>
                      <div className="checkbox-option">
                        <Checkbox
                          checked={isWillingToTravelFurther}
                          onChange={(e) => setIsWillingToTravelFurther(e.target.checked)}
                        >
                          I'm willing to travel further for the right event
                        </Checkbox>

                      </div>
                    </>
                  )}
                </LoadScript>
              </div>

              <div className="quiz-navigation">
                <Link
                  to="/"
                  className="nav-button exit-button"
                >
                  Exit
                </Link>
                <button
                  className="nav-button back-button"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  Back
                </button>
                <button
                  className="nav-button continue-button"
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={!checkQuestionAnswered(currentQuestionIndex)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Question 8: Availability */}
          {currentQuestionIndex >= 7 && (
            <div
              ref={setQuestionRef(7)}
              className={`question-slide ${currentQuestionIndex === 7 ? 'active' : ''}`}
            >
              <div className="question-header">
                <div className="question-number">Question 8 of {totalQuestions}</div>
                <h2>When are you typically available?</h2>
                <p className="question-description">Select all time slots that work for you.</p>
              </div>

              <div className="availability-grid">
                <div className="availability-header">
                  <div className="time-header-empty"></div>
                  {weekdays.map(day => (
                    <div key={day} className="day-header">{day}</div>
                  ))}
                </div>

                {timeSlots.map(slot => (
                  <div key={slot} className="availability-row">
                    <div className="time-label">{slot}</div>
                    {weekdays.map(day => (
                      <div
                        key={`${day}-${slot}`}
                        className={`time-slot ${availability.includes(`${day} ${slot}`) ? 'selected' : ''}`}
                        onClick={() => toggleAvailability(day, slot)}
                      >
                        {availability.includes(`${day} ${slot}`) && <CheckOutlined />}
                      </div>
                    ))}
                  </div>
                ))}

                <div className="checkbox-option">
                  <Checkbox
                    checked={isWillingToMakeTime}
                    onChange={(e) => setIsWillingToMakeTime(e.target.checked)}
                  >
                    I'm willing to make time for the right event
                  </Checkbox>
                </div>
              </div>

              <div className="quiz-navigation">
                <Link
                  to="/"
                  className="nav-button exit-button"
                >
                  Exit
                </Link>
                <button
                  className="nav-button back-button"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  Back
                </button>
                <button
                  className="nav-button continue-button"
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={!checkQuestionAnswered(currentQuestionIndex)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Question 9: Budget */}
          {currentQuestionIndex >= 8 && (
            <div
              ref={setQuestionRef(8)}
              className={`question-slide ${currentQuestionIndex === 8 ? 'active' : ''}`}
            >
              <div className="question-header">
                <div className="question-number">Question 9 of {totalQuestions}</div>
                <h2>What are your budget preferences for events?</h2>
                <p className="question-description">Select the option that best describes you.</p>
              </div>

              <div className="radio-options">
                <Radio.Group
                  onChange={(e) => setBudgetPreference(e.target.value)}
                  value={budgetPreference}
                  className="radio-group"
                >
                  <Space direction="vertical">
                    {budgetOptions.map(option => (
                      <Radio
                        key={option.value}
                        value={option.value}
                        className="radio-option"
                      >
                        {option.label}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </div>

              <div className="quiz-navigation">
                <Link
                  to="/"
                  className="nav-button exit-button"
                >
                  Exit
                </Link>
                <button
                  className="nav-button back-button"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  Back
                </button>
                <button
                  className="nav-button continue-button"
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={!checkQuestionAnswered(currentQuestionIndex)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Question 10: Similarity Importance */}
          {currentQuestionIndex >= 9 && (
            <div
              ref={setQuestionRef(9)}
              className={`question-slide ${currentQuestionIndex === 9 ? 'active' : ''}`}
            >
              <div className="question-header">
                <div className="question-number">Question 10 of {totalQuestions}</div>
                <h2>How important is it to meet people similar to you?</h2>
                <p className="question-description">Select the option that best describes your preference.</p>
              </div>

              <div className="radio-options">
                <Radio.Group
                  onChange={(e) => setSimilarityImportance(e.target.value)}
                  value={similarityImportance}
                  className="radio-group"
                >
                  <Space direction="vertical">
                    {similarityOptions.map(option => (
                      <Radio
                        key={option.value}
                        value={option.value}
                        className="radio-option"
                      >
                        {option.label}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </div>

              <div className="quiz-navigation">
                <Link
                  to="/"
                  className="nav-button exit-button"
                >
                  Exit
                </Link>
                <button
                  className="nav-button back-button"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  Back
                </button>
                <button
                  className="nav-button continue-button"
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={!checkQuestionAnswered(currentQuestionIndex)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Quiz Completion */}
          {quizCompleted && (
            <div className="completion-container">
              <div className="completion-content">
                <h2>Thanks for Sharing Your Preferences!</h2>
                <p>We'll use this information to personalize your Ummahfy experience and recommend events that match your interests.</p>

                {isUserLoggedIn ? (
                  <p>Click below to save your preferences to your account.</p>
                ) : (
                  <p>Create an account to save your preferences and discover events tailored just for you.</p>
                )}

                {!allQuestionsAnswered() && (
                  <p className="warning-text">
                    Please complete all questions before continuing.
                  </p>
                )}

                {submitError && (
                  <p className="error-text" style={{ color: 'red', marginTop: '10px' }}>
                    {submitError}
                  </p>
                )}

                <div className="quiz-navigation">
                  <Link
                    to="/"
                    className="nav-button exit-button"
                  >
                    Exit
                  </Link>
                  <button
                    className="nav-button back-button"
                    onClick={() => {
                      setQuizCompleted(false);
                      goToQuestion(totalQuestions - 1);
                    }}
                  >
                    Back
                  </button>
                  <button
                    className="create-button continue-button"
                    onClick={handleComplete}
                    disabled={!allQuestionsAnswered() || isSubmitting}
                  >
                    {isUserLoggedIn
                      ? (isSubmitting ? 'Saving...' : 'Save Answers')
                      : 'Create Account'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sign-in Modal shown directly on the quiz page */}
      <Signin
        trigger={showSigninModal}
        setTrigger={setShowSigninModal}
        onSuccess={() => handleSigninComplete(true)}
        onCancel={() => handleSigninComplete(false)}
      />
    </>
  );
}

export default OnboardingQuiz; 